/**
 * iter161 — Shared error-message normalizer.
 *
 * FastAPI / our backend returns errors in many shapes:
 *   - string:                    "Save failed"
 *   - {detail: "string"}
 *   - {detail: {code, message}}  ← Phase 19C preview-readonly middleware
 *   - {detail: [{loc, msg, type, input}]}  ← FastAPI 422 validation
 *   - {message: "..."}           ← some 3rd-party libs
 *   - {error: "..."}             ← legacy paths
 *   - axios network error (no response)
 *
 * Passing any of the object shapes directly to `toast.error()` or rendering
 * them into JSX crashes React with "Objects are not valid as a React child".
 *
 * Use this helper for every catch-block toast that surfaces a server error.
 *
 *   } catch (err) { toast.error(apiErrorMessage(err, 'Save failed')); }
 */
export function apiErrorMessage(err, fallback = 'Something went wrong') {
  if (!err) return fallback;
  if (typeof err === 'string') return err;

  const data = err?.response?.data;

  // FastAPI 422 — detail is an array of {loc, msg, type, input}
  if (Array.isArray(data?.detail)) {
    const msgs = data.detail
      .map((d) => (d && typeof d === 'object' ? d.msg || d.message : String(d)))
      .filter(Boolean);
    if (msgs.length) return msgs.join('; ');
  }

  // detail can be a plain string OR {code, message} (preview-readonly etc.)
  if (typeof data?.detail === 'string' && data.detail.trim()) return data.detail;
  if (data?.detail && typeof data.detail === 'object') {
    if (typeof data.detail.message === 'string' && data.detail.message.trim()) return data.detail.message;
    if (typeof data.detail.msg === 'string' && data.detail.msg.trim()) return data.detail.msg;
    if (typeof data.detail.error === 'string' && data.detail.error.trim()) return data.detail.error;
  }

  if (typeof data?.message === 'string' && data.message.trim()) return data.message;
  if (typeof data?.error === 'string' && data.error.trim()) return data.error;
  if (typeof err?.message === 'string' && err.message.trim()) return err.message;

  return fallback;
}

export default apiErrorMessage;
